import { useEffect, useCallback, useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import type { ActivityType } from '@/types';

export interface ActivityTemplate {
  id: string;
  dealership_id: string;
  name: string;
  activity_types: ActivityType[];
  sort_order: number;
  is_active: boolean;
}

// Demo-mode fallback so reps see chips even without a real backend
const DEMO_TEMPLATES: ActivityTemplate[] = [
  { id: 'demo-1', dealership_id: 'demo', name: 'Walkaround + Text Sent', activity_types: ['walkaround', 'text_sent'], sort_order: 0, is_active: true },
  { id: 'demo-2', dealership_id: 'demo', name: 'Test Drive + Quote Review', activity_types: ['test_drive', 'quote_review'], sort_order: 1, is_active: true },
  { id: 'demo-3', dealership_id: 'demo', name: 'Follow-up + Note', activity_types: ['followup_video', 'note_added'], sort_order: 2, is_active: true },
  { id: 'demo-4', dealership_id: 'demo', name: 'Email + Appointment', activity_types: ['email_sent', 'appointment_set'], sort_order: 3, is_active: true },
];

export function useActivityTemplates(opts: { activeOnly?: boolean } = {}) {
  const { activeOnly = true } = opts;
  const { mode, dealershipId } = useAuth();
  const [templates, setTemplates] = useState<ActivityTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (mode === 'demo') {
      setTemplates(activeOnly ? DEMO_TEMPLATES.filter(t => t.is_active) : DEMO_TEMPLATES);
      setLoading(false);
      return;
    }
    if (mode !== 'real' || !dealershipId) {
      setTemplates([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    let query = supabase
      .from('activity_templates')
      .select('*')
      .eq('dealership_id', dealershipId)
      .order('sort_order', { ascending: true })
      .order('created_at', { ascending: true });
    if (activeOnly) query = query.eq('is_active', true);
    const { data, error } = await query;
    if (!error && data) {
      setTemplates(
        data.map((r: any) => ({
          id: r.id,
          dealership_id: r.dealership_id,
          name: r.name,
          activity_types: (r.activity_types ?? []) as ActivityType[],
          sort_order: r.sort_order,
          is_active: r.is_active,
        })),
      );
    }
    setLoading(false);
  }, [mode, dealershipId, activeOnly]);

  useEffect(() => { load(); }, [load]);

  // Realtime: refresh on any template change for this dealership
  useEffect(() => {
    if (mode !== 'real' || !dealershipId) return;
    const channel = supabase
      .channel(`activity_templates:${dealershipId}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'activity_templates', filter: `dealership_id=eq.${dealershipId}` },
        () => { load(); },
      )
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [mode, dealershipId, load]);

  return { templates, loading, refresh: load };
}